import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Notaio } from '../../types';

interface ImmobileData {
  primaCasa: boolean;
  venditoreImpresa: boolean;
  prezzoAcquisto: string | null;
  renditaCatastale: string | null;
  percentualeAgenzia: string | null;
}

interface NotaioFormProps {
  notaio: Notaio | null;
  immobile?: ImmobileData | null;
  prezzoImmobile: number;
  onSave: (data: Partial<Notaio>) => Promise<void>;
  isLoading?: boolean;
}

export function NotaioForm({ notaio, immobile, prezzoImmobile, onSave, isLoading }: NotaioFormProps) {
  const [formData, setFormData] = useState({
    isPrimaCasa: immobile?.primaCasa ?? true,
    venditoreImpresa: immobile?.venditoreImpresa ?? false,
    percentualeAgenzia: immobile?.percentualeAgenzia ? parseFloat(immobile.percentualeAgenzia) : 3,
    catastale: immobile?.renditaCatastale ? parseFloat(immobile.renditaCatastale) : 0,
    conMutuo: true,
    onorarioCompravendita: 1200,
    onorarioMutuo: 650,
    visureVerifiche: 150,
    bolliDiritti: 230
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (notaio) {
      setFormData({
        isPrimaCasa: notaio.isPrimaCasa ?? true,
        venditoreImpresa: notaio.venditoreImpresa ?? false,
        percentualeAgenzia: notaio.percentualeAgenzia ?? 3,
        catastale: notaio.catastale ?? 0,
        conMutuo: notaio.conMutuo ?? true,
        onorarioCompravendita: notaio.onorarioCompravendita ?? 1200,
        onorarioMutuo: notaio.onorarioMutuo ?? 650,
        visureVerifiche: notaio.visureVerifiche ?? 150,
        bolliDiritti: notaio.bolliDiritti ?? 230
      });
    } else if (immobile) {
      setFormData(prev => ({
        ...prev,
        isPrimaCasa: immobile.primaCasa ?? prev.isPrimaCasa,
        venditoreImpresa: immobile.venditoreImpresa ?? prev.venditoreImpresa,
        percentualeAgenzia: immobile.percentualeAgenzia ? parseFloat(immobile.percentualeAgenzia) : prev.percentualeAgenzia,
        catastale: immobile.renditaCatastale ? parseFloat(immobile.renditaCatastale) : prev.catastale,
      }));
    }
  }, [notaio, immobile]);

  const handleChange = (field: string, value: number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-save on change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (notaio) {
        onSave(formData);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData, notaio]);

  return (
    <div className="space-y-6">
      {/* Tipo Acquisto */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Tipo Acquisto</h3>

        <div className="space-y-4">
          {/* Prima Casa Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-700">Prima Casa</label>
              <p className="text-sm text-gray-500">Agevolazioni fiscali prima casa</p>
            </div>
            <button
              type="button"
              onClick={() => handleChange('isPrimaCasa', !formData.isPrimaCasa)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                formData.isPrimaCasa ? 'bg-primary-600' : 'bg-gray-200'
              }`}
              disabled={isLoading}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  formData.isPrimaCasa ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Venditore Impresa Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-700">Acquisto da Impresa</label>
              <p className="text-sm text-gray-500">IVA invece di imposta registro</p>
            </div>
            <button
              type="button"
              onClick={() => handleChange('venditoreImpresa', !formData.venditoreImpresa)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                formData.venditoreImpresa ? 'bg-primary-600' : 'bg-gray-200'
              }`}
              disabled={isLoading}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  formData.venditoreImpresa ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Con Mutuo Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-700">Con Mutuo</label>
              <p className="text-sm text-gray-500">Includi atto mutuo e imposta sostitutiva</p>
            </div>
            <button
              type="button"
              onClick={() => handleChange('conMutuo', !formData.conMutuo)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                formData.conMutuo ? 'bg-primary-600' : 'bg-gray-200'
              }`}
              disabled={isLoading}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  formData.conMutuo ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Dati Immobile */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Dati Immobile</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prezzo Immobile
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">€</span>
              <input
                type="text"
                value={prezzoImmobile.toLocaleString('it-IT')}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                disabled
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Dalla{' '}
              <Link to="/immobile" className="text-blue-600 hover:underline">
                Scheda Immobile
              </Link>
            </p>
          </div>

          {!formData.venditoreImpresa && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rendita Catastale
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">€</span>
                <input
                  type="number"
                  value={formData.catastale}
                  onChange={(e) => handleChange('catastale', parseFloat(e.target.value) || 0)}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={isLoading}
                  min="0"
                  step="0.01"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Visura catastale o rogito</p>
            </div>
          )}
        </div>
      </div>

      {/* Costi Notarili */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Costi Notarili</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Onorario Compravendita
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">€</span>
              <input
                type="number"
                value={formData.onorarioCompravendita}
                onChange={(e) => handleChange('onorarioCompravendita', parseFloat(e.target.value) || 0)}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={isLoading}
                min="0"
              />
            </div>
          </div>

          {formData.conMutuo && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Onorario Atto Mutuo
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">€</span>
                <input
                  type="number"
                  value={formData.onorarioMutuo}
                  onChange={(e) => handleChange('onorarioMutuo', parseFloat(e.target.value) || 0)}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={isLoading}
                  min="0"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Visure e Verifiche
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">€</span>
              <input
                type="number"
                value={formData.visureVerifiche}
                onChange={(e) => handleChange('visureVerifiche', parseFloat(e.target.value) || 0)}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={isLoading}
                min="0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bolli e Diritti
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">€</span>
              <input
                type="number"
                value={formData.bolliDiritti}
                onChange={(e) => handleChange('bolliDiritti', parseFloat(e.target.value) || 0)}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={isLoading}
                min="0"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button (manual) */}
      {!notaio && (
        <button
          onClick={handleSave}
          disabled={isSaving || isLoading}
          className="w-full btn-primary"
        >
          {isSaving ? 'Salvataggio...' : 'Salva Configurazione'}
        </button>
      )}
    </div>
  );
}
